import React from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * Il disegno di un campo di scheda clinica, e le due regole che lo
 * accompagnano.
 *
 * STA IN UN FILE SUO perche' lo usano la procedura e il profilo del
 * paziente: la stessa scheda si compila in due posti e deve avere lo
 * stesso aspetto e le stesse regole. Due copie avrebbero smesso di
 * somigliarsi al primo campo nuovo, e un campo che si comporta in un modo
 * di qua e in un altro di la' e' il genere di differenza che si scopre da
 * un dato scritto male.
 */

export const chiave = (campoId, riga) => `${campoId}|${riga}`;

/* TOGLIERE UNA RIPETIZIONE, A SCHERMO, COME LA TOGLIE IL SERVER: via la
   riga dai campi di QUELLA sezione, le righe dopo scalano di uno, tutto
   il resto non si tocca. Si rifa' qui invece di ricaricare la scheda,
   perche' ricaricando si perderebbe quello che e' scritto e non ancora
   salvato nelle altre righe. Fino al 23 settembre 2026 il server
   cancellava la riga in tutta la compilazione: togliere la prima
   ripetizione cancellava anche ogni risposta delle sezioni normali. */
export const togliRipetizione = (valori, sezione, riga) => {
    const campi = new Set((sezione.campi || []).map((c) => String(c.campo_id)));
    const esito = {};
    for (const [k, v] of Object.entries(valori)) {
        const [campo, r] = k.split('|');
        const n = Number(r);
        if (!campi.has(campo)) { esito[k] = v; continue; }
        if (n === riga) continue;
        esito[chiave(campo, n > riga ? n - 1 : n)] = v;
    }
    return esito;
};

/* SALVATA UNA SCHEDA, L'AVVISO DEL PAZIENTE PUO' ESSERE CAMBIATO.
   La fascia rossa in cima alla scheda del paziente si ricava dalle
   compilazioni, e una spunta tolta qui la deve spegnere subito: lasciarla
   fino al prossimo F5 vuol dire mostrare una controindicazione che il
   medico ha appena smentito, che e' peggio di non mostrarla.
   Un evento e non un pacco di funzioni passate di mano in mano: fra chi
   salva e chi disegna la fascia ci sono due componenti che della cosa non
   sanno niente e non devono impararla. */
export { annunciaSchedaSalvata } from '../../utils/annunci';


/* NESSUNA SEGNALAZIONE «FUORI RIFERIMENTO», dal 21 settembre 2026.
   C'era: bordo giallo e la riga «riferimento 90-130 mmHg» sotto il
   campo. Confrontare un valore del paziente con un intervallo di
   normalita' e segnalare lo scostamento e' fornire informazioni per una
   decisione clinica (MDR, regola 11): RIMEDICHUB registra, non giudica.
   Il server non manda piu' riferimento_min/max, e questa schermata non
   li legge nemmeno se arrivassero: la rimozione qui e' la guardia vera,
   finche' il vincolo nel database non chiude anche la porta di dietro.
   Restano i limiti di plausibilita', che il server applica salvando. */

export const Campo = ({ campo, riga, valore, onChange, bloccato }) => {
    const id = `c${campo.campo_id}r${riga}`;

    /* La descrizione della risposta scelta: per i prodotti infusionali e'
       la controindicazione, e si vede solo quando quel prodotto e' stato
       scelto - prima sarebbe rumore, dopo sarebbe tardi. */
    const scelta = (campo.opzioni || []).find((o) => o.valore === valore);

    const comune = { id, disabled: bloccato, onChange: (e) => onChange(e.target.value) };

    return (
        <div className="sp-campo">
            <label htmlFor={id}>
                {campo.nome}
                {campo.unita && <span className="sp-unita"> ({campo.unita})</span>}
            </label>

            {campo.tipo === 'testo_lungo' ? (
                <textarea rows={3} value={valore ?? ''} {...comune} />
            ) : campo.tipo === 'scelta_singola' ? (
                <select value={valore ?? ''} {...comune}>
                    <option value="">—</option>
                    {(campo.opzioni || []).map((o) => (
                        <option key={o.valore} value={o.valore}>{o.etichetta}</option>
                    ))}
                </select>
            ) : campo.tipo === 'spunta' ? (
                <input type="checkbox" checked={valore === true || valore === 'true'}
                       disabled={bloccato}
                       onChange={(e) => onChange(e.target.checked)} />
            ) : campo.tipo === 'data' ? (
                <input type="date" value={(valore ?? '').toString().slice(0, 10)} {...comune} />
            ) : campo.tipo === 'numero' ? (
                <input type="number" step="any" value={valore ?? ''} {...comune} />
            ) : (
                <input type="text" value={valore ?? ''} {...comune} />
            )}

            {campo.aiuto && <small className="sp-aiuto">{campo.aiuto}</small>}
            {scelta?.descrizione && (
                <p className="sp-avviso">
                    <AlertTriangle size={13} aria-hidden="true" /> {scelta.descrizione}
                </p>
            )}
        </div>
    );
};

